import React, { useState, useEffect } from 'react';
import { 
    FiPlus, FiTrash2, FiEdit, FiZap, FiCalendar, FiPercent, 
    FiCheck, FiX, FiAlertCircle, FiTrendingUp, FiShoppingBag, FiInfo, FiTag
} from 'react-icons/fi';
import { saleService } from '../../../services/api';
import './AdminSales.css';

const AdminSales = () => {
    const [sales, setSales] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    // Imperial Deactivation States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [targetSale, setTargetSale] = useState(null);
    const [successMetadata, setSuccessMetadata] = useState({ title: '', message: '', icon: <FiCheck /> });

    // Form State
    const [form, setForm] = useState({
        title: '',
        discountPercentage: '',
        startDate: '',
        endDate: '',
        isActive: true
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const data = await saleService.getAllSales();
            let rawSales = Array.isArray(data) ? data : [data].filter(Boolean);
            
            const now = new Date();
            const validSales = [];

            // Auto-Cleanup: Filter and automatically delete expired sales
            for (const sale of rawSales) {
                if (sale.endDate && new Date(sale.endDate) < now) {
                    try {
                        const saleId = sale._id || sale.id;
                        if (saleId) await saleService.deleteSale(saleId);
                    } catch (err) {
                        console.error('Failed to auto-delete expired sale', err);
                    }
                } else {
                    validSales.push(sale);
                }
            }

            setSales(validSales);
        } catch (err) {
            console.error('Fetch error:', err);
            setErrorMsg('Failed to load sales.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Auto-dismiss feedback
    useEffect(() => {
        if (successMsg || errorMsg) {
            const t = setTimeout(() => {
                setSuccessMsg('');
                setErrorMsg('');
            }, 5000);
            return () => clearTimeout(t);
        }
    }, [successMsg, errorMsg]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'discountPercentage') {
            const regex = /^[0-9]*$/;
            if (value !== '' && !regex.test(value)) return;
        }

        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleOpenCreate = () => {
        setIsEditMode(false);
        setEditId(null);
        setForm({
            title: '',
            discountPercentage: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + 86400000 * 7).toISOString().split('T')[0],
            isActive: true
        });
        setShowModal(true);
    };

    const handleEdit = (s) => {
        const id = s._id || s.id;
        if (!id) {
            setErrorMsg("Sale ID missing.");
            return;
        }
        setIsEditMode(true);
        setEditId(id);
        setForm({
            title: s.title || '',
            discountPercentage: s.discountPercentage || '',
            startDate: s.startDate ? s.startDate.split('T')[0] : '',
            endDate: s.endDate ? s.endDate.split('T')[0] : '',
            isActive: s.isActive !== false
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // High-Fidelity Date Optimization: 
            // Ensure end date covers the full day (23:59:59) to prevent instant expiry on 1-day sales.
            const synchronizedForm = {
                ...form,
                startDate: new Date(form.startDate).toISOString(), 
                endDate: new Date(`${form.endDate}T23:59:59.999Z`).toISOString()
            };

            if (isEditMode) {
                if (!editId) throw new Error("Sale ID is required.");
                await saleService.updateSale(editId, synchronizedForm);
                setSuccessMetadata({
                    title: "Sale Updated",
                    message: `Changes for "${form.title}" saved successfully.`,
                    icon: <FiCheck />
                });
            } else {
                await saleService.createSale(synchronizedForm);
                setSuccessMetadata({
                    title: "Sale Added",
                    message: `"${form.title}" is now added and live.`,
                    icon: <FiCheck />
                });
            }
            setShowModal(false);
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
                fetchData();
            }, 2500);
        } catch (err) {
            setErrorMsg(err.response?.data?.message || 'Operation failed.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteRequest = (s) => {
        setTargetSale(s);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!targetSale) return;
        setIsSubmitting(true);
        try {
            await saleService.deleteSale(targetSale._id || targetSale.id);
            setShowDeleteModal(false);
            setSuccessMetadata({
                title: "Sale Deleted",
                message: `"${targetSale.title}" has been deleted.`,
                icon: <FiTrash2 />
            });
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
                setTargetSale(null);
                fetchData();
            }, 2500);
        } catch (err) {
            setErrorMsg("Delete failed. Please try again.");
            setShowDeleteModal(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="admin-sales-page">
            {/* Stats Banner */}
            <div className="sales-stats-banner">
                <div className="stat-pill active">
                    <FiZap /> <span>{sales.filter(s => s.isActive).length} ACTIVE SALES</span>
                </div>
                <div className="stat-pill scheduled">
                    <FiCalendar /> <span>SALE MANAGEMENT</span>
                </div>
            </div>

            <div className="sales-header">
                <div className="header-text">
                    <span className="hub-tag">Sale Hub</span>
                    <h2>Sale <span>Management</span></h2>
                </div>
                <button className="btn-create-sale" onClick={handleOpenCreate}>
                    <FiPlus /> Add New Sale
                </button>
            </div>

            {/* Feedback Alerts */}
            {successMsg && <div className="sale-alert success"><FiCheck /> {successMsg}</div>}
            {errorMsg && <div className="sale-alert error"><FiAlertCircle /> {errorMsg}</div>}

            {isLoading ? (
                <div className="sales-loader">
                    <div className="kinetic-ring"></div>
                    <span>Loading Sales...</span>
                </div>
            ) : (
                <div className="sales-grid">
                    {sales.length === 0 ? (
                        <div className="empty-sales">
                            <FiShoppingBag />
                            <h3>No Sales Found</h3>
                            <p>Add your first sale to start.</p>
                        </div>
                    ) : (
                        sales.map((sale, idx) => (
                            <div key={sale._id || idx} className="sale-card-premium" style={{ animationDelay: `${idx * 0.1}s` }}>
                                <div className="sale-card-top">
                                    <div className={`status-indicator ${sale.isActive ? 'active' : 'inactive'}`}>
                                        {sale.isActive ? 'ACTIVE' : 'INACTIVE'}
                                    </div>
                                    <div className="sale-discount-badge">{sale.discountPercentage}%</div>
                                </div>
                                <h3 className="sale-title">{sale.title}</h3>
                                <div className="sale-meta-row">
                                    <div className="meta-item">
                                        <FiCalendar /> <span>{new Date(sale.startDate).toLocaleDateString()}</span>
                                    </div>
                                    <div className="meta-arrow">→</div>
                                    <div className="meta-item">
                                        <FiCalendar /> <span>{new Date(sale.endDate).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <div className="sale-card-footer">
                                    <button className="btn-sale-action edit" onClick={() => handleEdit(sale)}>
                                        <FiEdit /> Edit
                                    </button>
                                    <button className="btn-sale-action delete" onClick={() => handleDeleteRequest(sale)}>
                                        <FiTrash2 /> Delete
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Premium Modal Architecture */}
            {showModal && (
                <div className="sale-modal-overlay active">
                    <div className="sale-modal-glass">
                        <div className="modal-header">
                            <h3>{isEditMode ? 'Edit Sale' : 'Add New Sale'}</h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="sale-form">
                            <div className="form-group">
                                <label><FiTag /> Sale Title</label>
                                <input 
                                    name="title" 
                                    value={form.title} 
                                    onChange={handleInputChange} 
                                    placeholder="Enter sale title..." 
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label><FiPercent /> Discount Percentage</label>
                                <input 
                                    type="text" 
                                    name="discountPercentage" 
                                    value={form.discountPercentage} 
                                    onChange={handleInputChange} 
                                    onFocus={(e) => {
                                        e.target.placeholder = '';
                                        if (form.discountPercentage === '0' || form.discountPercentage === 0) {
                                            setForm(prev => ({ ...prev, discountPercentage: '' }));
                                        }
                                    }}
                                    onBlur={(e) => e.target.placeholder = '0'}
                                    placeholder="0"
                                    required 
                                />
                            </div>
                            <div className="date-grid">
                                <div className="form-group">
                                    <label><FiCalendar /> Start Date</label>
                                    <input 
                                        type="date" 
                                        name="startDate" 
                                        value={form.startDate} 
                                        onChange={handleInputChange} 
                                        required 
                                    />
                                </div>
                                <div className="form-group">
                                    <label><FiCalendar /> End Date</label>
                                    <input 
                                        type="date" 
                                        name="endDate" 
                                        value={form.endDate} 
                                        onChange={handleInputChange} 
                                        required 
                                    />
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Sale'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Imperial Deactivation Modal */}
            {showDeleteModal && (
                <div className="sale-modal-overlay active">
                    <div className="sale-modal-glass deactivation-modal">
                        <div className="deactivation-header">
                            <div className="warning-icon-shell">
                                <FiAlertCircle />
                            </div>
                            <h3>Delete Sale</h3>
                        </div>
                        <div className="deactivation-body">
                            <p>Are you sure you want to delete <strong>"{targetSale?.title}"</strong>?</p>
                            <span className="warning-note">This sale will be removed from the store.</span>
                        </div>
                        <div className="deactivation-actions">
                            <button className="btn-cancel-deactivate" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-confirm-deactivate" onClick={confirmDelete} disabled={isSubmitting}>
                                {isSubmitting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kinetic Success Feedback Modal */}
            {showSuccessModal && (
                <div className="sale-modal-overlay active success-overlay">
                    <div className="success-modal-kinetic">
                        <div className="success-check-shell">
                            {successMetadata.icon}
                        </div>
                        <h3>{successMetadata.title}</h3>
                        <p>{successMetadata.message}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSales;

